import React, { useState, useEffect, lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { auth } from "./firebase";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ChunkErrorBoundary, { ClearChunkReloadFlag } from "./components/ChunkErrorBoundary";
import useAnalytics from "./hooks/useAnalytics";
import "./styles/feed.css";
import "./styles/shared-icons.css";
import "./styles/about.css";
import "./App.css";
import "./styles/reading.css";

const HomePage = lazy(() => import("./pages/Home"));
const Admin = lazy(() => import("./pages/Admin"));
const PublicProfile = lazy(() => import("./pages/PublicProfile"));
const SignUp = lazy(() => import("./pages/SignUp"));
const ForgotPass = lazy(() => import("./pages/ForgotPass"));
const AllPersonas = lazy(() => import("./components/Persona/AllPersonas"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const ReleaseNotes = lazy(() => import("./pages/ReleaseNotes"));
const KPIs = lazy(() => import("./pages/KPIs"));
const EmailVerification = lazy(() => import("./pages/EmailVerification"));
const CompleteSignup = lazy(() => import("./pages/CompleteSignup"));
const BookDetail = lazy(() => import("./pages/BookDetail"));
const BuddyRead = lazy(() => import("./pages/BuddyRead"));
const BuddyInviteAccept = lazy(() => import("./pages/BuddyInviteAccept"));
const About = lazy(() => import("./pages/About"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let settled = false;
    const finish = (currentUser) => {
      settled = true;
      setUser(currentUser);
      setLoading(false);
    };
    const unsubscribe = auth.onAuthStateChanged(
      (currentUser) => finish(currentUser),
      () => finish(null)
    );
    const timeout = setTimeout(() => {
      if (!settled) finish(null);
    }, 8000);
    return () => {
      unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  if (loading) return <div className="app-loading" />;

  return (
    <Router>
      <AppContent user={user} />
    </Router>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppContent({ user }) {
  // ✅ Initialize Analytics tracking inside Router context
  useAnalytics();

  // ✅ Store acquisition data on first visit
  useEffect(() => {
    const { storeAcquisitionData } = require('./utils/analytics');
    storeAcquisitionData();
  }, []); // Only run once on mount


  return (
    <div className="app-container">
      <ScrollToTop />
      <Navbar user={user} />
      <main>
        <ChunkErrorBoundary>
        <Suspense fallback={<div className="app-loading" />}>
          <ClearChunkReloadFlag />
          <Routes>
            {/* ✅ Public Routes */}
            <Route path="/" element={<HomePage user={user} />} />
            <Route path="/signup" element={<SignUp />} />
            {/* <Route path="/top-ten" element={<TopTen />} /> */}
            <Route path="/all-personas" element={<AllPersonas />} />
            <Route path="/terms-of-use" element={<Terms />} />
            <Route path="/privacy-policy" element={<Privacy />} />
            <Route path="/about" element={<About />} />
            <Route path="/release-notes" element={<ReleaseNotes />} />
            <Route path="/forgotpassword" element={<ForgotPass />} />
            <Route path="/personas" element={<AllPersonas />} />
            <Route path="/kpis" element={<KPIs />} />
            <Route path="/verify-email" element={<EmailVerification />} />
            <Route path="/complete-signup" element={<CompleteSignup />} />
            <Route path="/admin" element={user ? <Admin /> : <Navigate to="/" />} />

            {/* Public unsubscribe / resubscribe handler (no auth required) */}
            <Route path="/unsubscribe/:token" element={<Unsubscribe />} />
            
            {/* ✅ Book Detail Route */}
            <Route path="/book/:bookTitle" element={<BookDetail />} />
            
            {/* ✅ Buddy Read Routes */}
            <Route path="/buddy/invite/:token" element={<BuddyInviteAccept />} />
            <Route path="/buddy/:buddyReadId" element={<BuddyRead />} />
            
            {/* ✅ Public Profile Routes */}
            <Route path="/:username" element={<PublicProfile />} />
            <Route path="/:username/reads" element={<PublicProfile />} />
            <Route path="/:username/stack" element={<PublicProfile />} />
            <Route path="/:username/stats" element={<PublicProfile />} />
            <Route path="/:username/notifications" element={<PublicProfile />} />
            <Route path="/:username/admin/*" element={user ? <Admin /> : <Navigate to="/" />} />
            
          </Routes>
        </Suspense>
        </ChunkErrorBoundary>
        </main>

        <Footer />
      </div>
    );
}

export default App;